import Vue from 'vue'

const ITEM_TRANSITION_NAME = 'ds-item-active'
const ITEM_TRANSITION_ATTRIBUTE = 'data-vt-item-transitioning'
const DEFAULT_DESTINATION_TIMEOUT_MS = 2000
const CANVAS_HANDOFF_MS = 300

let context = null

function esc(value) {
  return CSS.escape(String(value))
}

function itemVisuals(itemId) {
  return [...document.querySelectorAll(`[data-vt-item-visual="${esc(itemId)}"]`)]
}

function findItemVisual(itemId, exclude = null) {
  const candidates = itemVisuals(itemId).filter(element =>
    element !== exclude && !element.closest('[data-vt-layout-pending]')
  )
  // A retained renderer thumbnail is the exact still represented by the
  // WebGL scene. Prefer it over any duplicate thumbnail elsewhere on screen.
  return candidates.find(element => element.classList.contains('frame-renderer__thumbnail'))
    || candidates[0]
    || null
}

function imageSource(element) {
  if (element?.tagName !== 'IMG') return ''
  return element.currentSrc || element.getAttribute('src') || ''
}

function isLoadedImage(element) {
  if (element?.tagName !== 'IMG') return true
  return !!imageSource(element) && element.complete && Number(element.naturalWidth) > 0
}

function prepareVisual(element) {
  const canvas = element.closest('.frame-renderer')
    ?.querySelector('.frame-renderer__canvas-wrapper') || null
  const record = {
    element,
    canvas,
    visibility: element.style.visibility,
    transitionName: element.style.viewTransitionName || '',
    transitionAttribute: element.getAttribute(ITEM_TRANSITION_ATTRIBUTE),
    canvasOpacity: canvas?.style.opacity ?? '',
    canvasTransition: canvas?.style.transition ?? '',
  }

  // The shared element is always a still image. A retained renderer thumbnail
  // may normally be hidden beneath WebGL, so reveal the still and suppress the
  // canvas only for snapshot capture. The canvas itself is never named or
  // captured as the shared transition element.
  element.style.visibility = 'visible'
  element.style.viewTransitionName = ITEM_TRANSITION_NAME
  // FrameRenderer updates its retained thumbnail styles after camera fitting.
  // Keep our still visible through the browser's new-state snapshot even if
  // that reactive update restores the thumbnail's normal hidden state.
  element.setAttribute(ITEM_TRANSITION_ATTRIBUTE, '')
  if (canvas) {
    canvas.style.transition = 'none'
    canvas.style.opacity = '0'
  }
  return record
}

function restoreVisual(record, { handoffToCanvas = false } = {}) {
  if (!record) return

  // Hold the still over FrameRenderer's normal canvas fade. Crossfading two
  // different images changes their combined brightness; releasing the still
  // only after the canvas is opaque avoids that animated flash.
  if (handoffToCanvas && record.element.animate) {
    record.element.animate(
      [{ visibility: 'visible', opacity: 1 }, { visibility: 'visible', opacity: 1 }],
      { duration: CANVAS_HANDOFF_MS }
    )
  } else {
    record.element.style.visibility = record.visibility
  }
  record.element.style.viewTransitionName = record.transitionName
  if (record.transitionAttribute == null) {
    record.element.removeAttribute(ITEM_TRANSITION_ATTRIBUTE)
  } else {
    record.element.setAttribute(ITEM_TRANSITION_ATTRIBUTE, record.transitionAttribute)
  }
  if (record.canvas) {
    record.canvas.style.opacity = record.canvasOpacity
    record.canvas.style.transition = record.canvasTransition
  }
}

/**
 * Return the one item represented on both sides of a navigation. Different
 * non-null ids have no shared visual and therefore should not transition.
 */
export function sharedItemId(fromItemId, toItemId) {
  if (fromItemId != null && toItemId != null) {
    return String(fromItemId) === String(toItemId) ? fromItemId : null
  }
  return fromItemId ?? toItemId ?? null
}

/** Resolve an item transition for cross-route and same-route query changes. */
export function navigationItemId({ fromRouteName, toRouteName, fromItemId, toItemId }) {
  const itemId = sharedItemId(fromItemId, toItemId)
  if (itemId == null) return null
  const itemStateChanged = String(fromItemId ?? '') !== String(toItemId ?? '')
  return fromRouteName !== toRouteName || itemStateChanged ? itemId : null
}

/** Prepare the old-state still image synchronously, before VT captures it. */
export function startItemTransition(itemId) {
  cleanup()
  const source = findItemVisual(itemId)
  if (!source || !isLoadedImage(source)) return false
  context = {
    itemId,
    source,
    sourceRecord: prepareVisual(source),
    destinationRecord: null,
  }
  return true
}

/**
 * Wait for Vue/router rendering and image loading, then mark the destination
 * still. This replaces per-page `signalReady` callbacks with one DOM contract.
 */
export async function waitForItemDestination({ timeoutMs = DEFAULT_DESTINATION_TIMEOUT_MS } = {}) {
  const active = context
  if (!active) return
  await Vue.nextTick()

  const destination = await new Promise((resolve) => {
    let observedImage = null
    let observer = null
    let timer = null

    const dispose = () => {
      observer?.disconnect()
      if (timer) clearTimeout(timer)
      if (observedImage) {
        observedImage.removeEventListener('load', scan)
        observedImage.removeEventListener('error', fail)
      }
    }
    const finish = (element = null) => {
      dispose()
      resolve(element)
    }
    const fail = () => finish(null)
    const watchImage = (element) => {
      if (observedImage === element) return
      if (observedImage) {
        observedImage.removeEventListener('load', scan)
        observedImage.removeEventListener('error', fail)
      }
      observedImage = element
      element.addEventListener('load', scan, { once: true })
      element.addEventListener('error', fail, { once: true })
    }
    function scan() {
      if (context !== active) return finish(null)
      const element = findItemVisual(active.itemId, active.source)
      if (!element) return
      if (element.tagName === 'IMG' && !imageSource(element)) return finish(null)
      if (isLoadedImage(element)) return finish(element)
      watchImage(element)
    }

    observer = new MutationObserver(scan)
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-vt-item-visual', 'data-vt-layout-pending', 'src'],
    })
    timer = setTimeout(() => finish(null), timeoutMs)
    scan()
  })

  if (context !== active) return
  // The old snapshot has already been captured before the update callback.
  restoreVisual(active.sourceRecord)
  active.sourceRecord = null
  if (destination) active.destinationRecord = prepareVisual(destination)
  await Vue.nextTick()
}

/** Restore the still and canvas styles after the native transition finishes. */
export function cleanup() {
  if (!context) return
  restoreVisual(context.sourceRecord)
  restoreVisual(context.destinationRecord, {
    handoffToCanvas: !!context.destinationRecord?.canvas,
  })
  context = null
}
